import { ReactNode, useEffect, useRef, useState } from 'react';

interface Props<T> {
  reels: number;
  rows: number;
  grid: T[][]; // результат [reel][row]
  spinning: boolean;
  winCells: Set<string>;
  renderCell: (cell: T) => ReactNode;
  randomCell: () => T;
  extraCellClass?: (cell: T) => string;
}

// Настоящая прокрутка барабанов: при spinning лента крутится бесшовно,
// при остановке барабаны замирают слева-направо, показывая результат.
export function ReelGrid<T>({
  reels,
  rows,
  grid,
  spinning,
  winCells,
  renderCell,
  randomCell,
  extraCellClass,
}: Props<T>) {
  const [stopped, setStopped] = useState(reels);
  const strips = useRef<T[][]>([]);

  useEffect(() => {
    if (spinning) {
      // юнит из rows*3 символов; рендерим дважды → бесшовный скролл (translateY -50%)
      strips.current = Array.from({ length: reels }, () =>
        Array.from({ length: rows * 3 }, randomCell)
      );
      setStopped(0);
      return;
    }
    // остановка барабанов по очереди
    setStopped(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let r = 0; r < reels; r++) timers.push(setTimeout(() => setStopped(r + 1), r * 150));
    return () => timers.forEach(clearTimeout);
  }, [spinning, reels, rows]);

  return (
    <div className="reels" style={{ gridTemplateColumns: `repeat(${reels}, 1fr)` }}>
      {Array.from({ length: reels }).map((_, r) => (
        <div className="reel" key={r}>
          {r < stopped || !spinning ? (
            <div className="reel-strip">
              {(grid[r] ?? []).map((cell, row) => (
                <div
                  className={`reel-cell settle ${winCells.has(`${r}-${row}`) ? 'hit' : ''} ${extraCellClass ? extraCellClass(cell) : ''}`}
                  style={{ animationDelay: `${row * 45}ms` }}
                  key={row}
                >
                  {renderCell(cell)}
                </div>
              ))}
            </div>
          ) : (
            <div className="reel-strip rolling" style={{ animationDuration: `${0.34 + r * 0.04}s` }}>
              {[...(strips.current[r] ?? []), ...(strips.current[r] ?? [])].map((cell, i) => (
                <div className="reel-cell" key={i}>
                  {renderCell(cell)}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
